import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AlertCard from './AlertCard';
import BlazingFastCheckbox from './BlazingFastCheckbox';
import AlertBadge from './AlertBadge';
import AlertTypeBadge from './AlertTypeBadge';
import SelectionContext from '../../contexts/SelectionContext';

/**
 * SelectableAlertCard - Alert card with selection capability
 * Phase 5: Wrapper around AlertCard with checkbox, badges, and selection state
 */
const SelectableAlertCard = ({
  // Core props
  item,
  alertKey,
  clientId,
  
  // Display props
  showMachineId = true,
  showBadge = true,
  handleMachineIdClick,
  
  // All other AlertCard props
  ...alertCardProps
}) => {
  const { t } = useTranslation();
  
  
  // Get selection state from context - use React.useContext directly to handle optional context
  const selectionContext = React.useContext(SelectionContext);
  
  // Default empty functions if no context
  const isSelected = selectionContext?.isSelected || (() => false);
  const toggleSelection = selectionContext?.toggleSelection || (() => {});
  
  const isThisCardSelected = isSelected(item.id);
  
  // We need to manually build the controls to insert selection UI
  const headerControls = (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      {/* Left side - checkbox, machine ID, status badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        <BlazingFastCheckbox
          experimentId={item.id}
          isSelected={isThisCardSelected}
          onToggleSelection={() => toggleSelection(item.id)}
        />
        
        {/* Machine ID Pill - matching experiment card */}
        {showMachineId && item.machine_id && (
          <Tooltip 
            title={`${(item.group || item.store?.group || '')} - ${(item.location || item.store?.location || '')}`.trim() || item.store_cluster_id || (t ? t('machineCluster') : 'Machine Cluster')} 
            arrow
          >
            <Box
              onClick={handleMachineIdClick ? () => handleMachineIdClick(item) : undefined}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '20px',
                px: '8px',
                backgroundColor: 'rgba(107, 114, 128, 0.08)',
                borderRadius: '10px',
                border: '1px solid rgba(107, 114, 128, 0.12)',
                transition: 'all 0.2s',
                cursor: handleMachineIdClick ? 'pointer' : 'default',
                '&:hover': handleMachineIdClick ? {
                  backgroundColor: 'rgba(107, 114, 128, 0.12)',
                  borderColor: 'rgba(107, 114, 128, 0.2)',
                } : {}
              }}
            >
              <Typography sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6b7280',
                lineHeight: 1,
              }}>
                {item.machine_id}
              </Typography>
            </Box>
          </Tooltip>
        )}
        
        {/* Alert Status Badge */}
        {showBadge && (
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            height: '20px',
            px: '6px',
            backgroundColor: getStatusBackgroundColor(item),
            borderRadius: '10px',
            border: `1px solid ${getStatusBorderColor(item)}`,
          }}>
            <AlertBadge alert={item} t={t} />
            <Typography sx={{
              fontSize: '8px',
              fontWeight: 600,
              color: getStatusTextColor(item),
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              lineHeight: 1,
            }}>
              {getStatusText(item, t)}
            </Typography>
          </Box>
        )}
        
        {/* Alert Type Badge */}
        {item.alert_type && (
          <AlertTypeBadge alertType={item.alert_type} t={t} />
        )}
      </Box>
      
      {/* Right side - empty for now, could add menu later */}
      <Box />
    </Box>
  );
  
  // Render the card with header controls
  return (
    <AlertCard
      {...alertCardProps}
      item={item}
      alertKey={alertKey}
      clientId={clientId}
      headerControls={headerControls}
      focusedItemId={isThisCardSelected ? item.id : null}
      t={t} // Pass translation function to AlertCard
    />
  );
};

// Helper functions for status styling
const getStatusBackgroundColor = (alert) => {
  switch (alert.status) {
    case 'resolved':
    case 'not_apply':
      return 'rgba(52, 199, 89, 0.05)'; // Apple green for resolved/working
    case 'under_review':
      return 'rgba(255, 149, 0, 0.05)'; // Apple orange for under review
    case 'pending':
    default:
      return 'rgba(142, 142, 147, 0.05)'; // Apple gray for pending
  }
};

const getStatusBorderColor = (alert) => {
  switch (alert.status) {
    case 'resolved':
    case 'not_apply':
      return 'rgba(52, 199, 89, 0.2)'; // Apple green for resolved/working
    case 'under_review':
      return 'rgba(255, 149, 0, 0.2)'; // Apple orange for under review
    case 'pending':
    default:
      return 'rgba(142, 142, 147, 0.2)'; // Apple gray for pending
  }
};

const getStatusTextColor = (alert) => {
  switch (alert.status) {
    case 'resolved':
    case 'not_apply':
      return '#34C759'; // Apple green for resolved/working
    case 'under_review':
      return '#FF9500'; // Apple orange for under review
    case 'pending':
    default:
      return '#8E8E93'; // Apple gray for pending
  }
};

const getStatusText = (alert, t) => {
  // Use the status field for accurate status determination
  switch (alert.status) {
    case 'resolved':
      return t ? t('resolved') : 'Resolved';
    case 'under_review':
      return t ? t('underReview') : 'Under Review';
    case 'not_apply':
      return t ? t('notBroken') : 'Working';
    case 'pending':
    default:
      return t ? t('pending') : 'Pending';
  }
};


export default SelectableAlertCard;
